import React, { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ColorPicker from './ColorPicker';
import { Select } from '../../components/form-fields/Select';
import Toggle from '../../../components/common/toggleButton';
import { FieldLabel } from '@measured/puck';
import CustomTooltip from '../../../components/common/tooltip';
import InputField from '../../../components/common/input-field';
import { getNumberPattern } from '../../utils/helper';

export interface BorderProps {
  value: IBorderValueProps;
  onChange: (_value: IBorderValueProps) => void;
  title: string;
  showWidthProps?: boolean;
}

export const borderProps = {
  border: {
    deviceType: 'desktop',
    borderBottomLeft: {
      desktop: '0',
      tablet: '0',
      mobile: '0',
    },
    borderBottomRight: {
      desktop: '0',
      tablet: '0',
      mobile: '0',
    },
    borderTopLeft: {
      desktop: '0',
      tablet: '0',
      mobile: '0',
    },
    borderTopRight: {
      desktop: '0',
      tablet: '0',
      mobile: '0',
    },
    borderTopWidth: {
      desktop: 0,
      tablet: 0,
      mobile: 0,
    },
    borderRightWidth: {
      desktop: 0,
      tablet: 0,
      mobile: 0,
    },
    borderBottomWidth: {
      desktop: 0,
      tablet: 0,
      mobile: 0,
    },
    borderLeftWidth: {
      desktop: 0,
      tablet: 0,
      mobile: 0,
    },
    borderWidth: {
      desktop: 0,
      tablet: 0,
      mobile: 0,
    },
    borderStyle: {
      desktop: 'border-solid',
      tablet: 'border-solid',
      mobile: 'border-solid',
    },
    borderColor: {
      desktop: '#000000',
      tablet: '#000000',
      mobile: '#000000',
    },
  },
};

export interface IBorderValueProps {
  borderTopLeft: {
    [key: string]: string;
    mobile: string;
    tablet: string;
    desktop: string;
  };
  borderTopRight: {
    [key: string]: string;
    mobile: string;
    tablet: string;
    desktop: string;
  };
  borderBottomLeft: {
    [key: string]: string;
    mobile: string;
    tablet: string;
    desktop: string;
  };
  borderBottomRight: {
    [key: string]: string;
    mobile: string;
    tablet: string;
    desktop: string;
  };
  borderTopWidth?: {
    [key: string]: number;
    mobile: number;
    tablet: number;
    desktop: number;
  };
  borderRightWidth?: {
    [key: string]: number;
    mobile: number;
    tablet: number;
    desktop: number;
  };
  borderBottomWidth?: {
    [key: string]: number;
    mobile: number;
    tablet: number;
    desktop: number;
  };
  borderLeftWidth?: {
    [key: string]: number;
    mobile: number;
    tablet: number;
    desktop: number;
  };
  borderWidth: {
    [key: string]: number;
    mobile: number;
    tablet: number;
    desktop: number;
  };
  borderColor: {
    [key: string]: string;
    mobile: string;
    tablet: string;
    desktop: string;
  };
  borderStyle: {
    [key: string]: string;
    mobile: string;
    tablet: string;
    desktop: string;
  };
  deviceType: string;
}

const borderStyleOptions = [
  { label: 'None', value: 'border-none' },
  { label: 'Solid', value: 'border-solid' },
  { label: 'Dashed', value: 'border-dashed' },
  { label: 'Dotted', value: 'border-dotted' },
  { label: 'Double', value: 'border-double' },
];

const Border: React.FC<BorderProps> = ({
  title,
  value,
  onChange,
  showWidthProps = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div>
      <div
        className="flex justify-between font-bold cursor-pointer py-2 bg-slate-100 rounded-md pagebuilder-theme"
        onClick={() => setIsOpen(!isOpen)}>
        <h3>{title}</h3>
        {!isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
      </div>
      {isOpen && (
        <div className="flex flex-col gap-4 mt-3  border border-gray-300 rounded p-2 w-full">
          <Toggle<IBorderValueProps>
            value={value}
            onChange={newValue => onChange({ ...value, ...newValue })}
            heading={{ label: 'Device', value: 'device' }}
            options={[
              { label: 'Desktop', value: 'desktop' },
              { label: 'Tablet', value: 'tablet' },
              { label: 'Mobile', value: 'mobile' },
            ]}
            propName="deviceType"
          />
          <ColorPicker
            value={value?.borderColor?.[value?.deviceType]}
            onChange={color =>
              onChange({
                ...value,
                borderColor: {
                  ...value?.borderColor,
                  [value?.deviceType]: color,
                },
              })
            }
            label={'Border Color'}
            tooltip={'Border Color'}
          />
          <FieldLabel
            label={
              (
                <CustomTooltip title="Border Radius">
                  <span>Border Radius</span>
                </CustomTooltip>
              ) as React.ReactNode as string
            }>
            <div className="grid grid-cols-[1fr_1fr] gap-2 border-t-1">
              <InputField
                label="Top-Left"
                value={value?.borderTopLeft?.[value?.deviceType]}
                className="h-[42px]"
                onChange={e => {
                  const newValue = e.replace(/[^0-9%]/g, '');
                  onChange({
                    ...value,
                    borderTopLeft: {
                      ...value?.borderTopLeft,
                      [value?.deviceType]: newValue,
                    },
                  });
                }}
              />
              <InputField
                className="h-[42px]"
                value={value?.borderTopRight?.[value?.deviceType]}
                onChange={e => {
                  const newValue = e.replace(/[^0-9%]/g, '');
                  onChange({
                    ...value,
                    borderTopRight: {
                      ...value?.borderTopRight,
                      [value?.deviceType]: newValue,
                    },
                  });
                }}
                label="Top-Right"
              />
              <InputField
                className="h-[42px]"
                value={value?.borderBottomLeft?.[value?.deviceType]}
                onChange={e => {
                  const newValue = e.replace(/[^0-9%]/g, '');
                  onChange({
                    ...value,
                    borderBottomLeft: {
                      ...value?.borderBottomLeft,
                      [value?.deviceType]: newValue,
                    },
                  });
                }}
                label="Bottom-Left"
              />
              <InputField
                className="h-[42px]"
                value={value?.borderBottomRight?.[value?.deviceType]}
                onChange={e => {
                  const newValue = e.replace(/[^0-9%]/g, '');
                  onChange({
                    ...value,
                    borderBottomRight: {
                      ...value?.borderBottomRight,
                      [value?.deviceType]: newValue,
                    },
                  });
                }}
                label="Bottom-Right"
              />
            </div>
          </FieldLabel>
          {showWidthProps ? (
            <FieldLabel
              label={
                (
                  <CustomTooltip title="Border Width">
                    <span>Border Width</span>
                  </CustomTooltip>
                ) as React.ReactNode as string
              }>
              <div className="grid grid-cols-[1fr_1fr] gap-2 border-t-1">
                <InputField
                  className="h-[42px]"
                  value={value?.borderTopWidth?.[value?.deviceType]}
                  onChange={width => {
                    const newValue = getNumberPattern(width);
                    if (!value?.borderTopWidth) {
                      value.borderTopWidth = {
                        desktop: 0,
                        tablet: 0,
                        mobile: 0,
                      };
                    }
                    newValue &&
                      onChange({
                        ...value,
                        borderTopWidth: {
                          ...value?.borderTopWidth,
                          [value?.deviceType]: +newValue,
                        },
                      });
                  }}
                  label="Top"
                />

                <InputField
                  className="h-[42px]"
                  value={value?.borderRightWidth?.[value?.deviceType]}
                  onChange={width => {
                    const newValue = getNumberPattern(width);
                    if (!value?.borderRightWidth) {
                      value.borderRightWidth = {
                        desktop: 0,
                        tablet: 0,
                        mobile: 0,
                      };
                    }
                    newValue &&
                      onChange({
                        ...value,
                        borderRightWidth: {
                          ...value?.borderRightWidth,
                          [value?.deviceType]: +newValue,
                        },
                      });
                  }}
                  label="Right"
                />

                <InputField
                  className="h-[42px]"
                  value={value?.borderBottomWidth?.[value?.deviceType]}
                  onChange={width => {
                    const newValue = getNumberPattern(width);
                    if (!value?.borderBottomWidth) {
                      value.borderBottomWidth = {
                        desktop: 0,
                        tablet: 0,
                        mobile: 0,
                      };
                    }
                    newValue &&
                      onChange({
                        ...value,
                        borderBottomWidth: {
                          ...value?.borderBottomWidth,
                          [value?.deviceType]: +newValue,
                        },
                      });
                  }}
                  label="Bottom"
                />

                <InputField
                  className="h-[42px]"
                  value={value?.borderLeftWidth?.[value?.deviceType]}
                  onChange={width => {
                    const newValue = getNumberPattern(width);
                    if (!value?.borderLeftWidth) {
                      value.borderLeftWidth = {
                        desktop: 0,
                        tablet: 0,
                        mobile: 0,
                      };
                    }
                    newValue &&
                      onChange({
                        ...value,
                        borderLeftWidth: {
                          ...value?.borderLeftWidth,
                          [value?.deviceType]: +newValue,
                        },
                      });
                  }}
                  label="Left"
                />
              </div>
            </FieldLabel>
          ) : (
            <FieldLabel
              label={
                (
                  <CustomTooltip title="Border Width">
                    <span>Border Width</span>
                  </CustomTooltip>
                ) as React.ReactNode as string
              }>
              <InputField
                className="h-[42px]"
                value={value?.borderWidth?.[value?.deviceType]}
                onChange={width => {
                  const newValue = getNumberPattern(width);
                  newValue &&
                    onChange({
                      ...value,
                      borderWidth: {
                        ...value?.borderWidth,
                        [value?.deviceType]: +newValue,
                      },
                    });
                }}
              />
            </FieldLabel>
          )}
          <FieldLabel
            label={
              (
                <CustomTooltip title="Border Style">
                  <span>Border Style</span>
                </CustomTooltip>
              ) as React.ReactNode as string
            }>
            <Select
              className="pagebuilder-theme rounded-md"
              value={value?.borderStyle?.[value?.deviceType]}
              onChange={e =>
                onChange({
                  ...value,
                  borderStyle: {
                    ...value?.borderStyle,
                    [value?.deviceType]: e.target.value,
                  },
                })
              }
              options={borderStyleOptions}
            />
          </FieldLabel>
        </div>
      )}
    </div>
  );
};

export default Border;
